import React from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import { enGB } from 'date-fns/locale';
import { isValid } from 'date-fns';
import { getUserTimezone } from '../../utils/timezone';

/**
 * Date filter input that emits YYYY-MM-DD strings with timezone metadata
 * 
 * User-selected dates represent calendar days in the user's timezone.
 * The date string is sent along with timezone info so backend can expand
 * it correctly to cover the entire calendar day in the user's timezone.
 * 
 * @param {string} value - YYYY-MM-DD format string (calendar day in user's timezone)
 * @param {function} onChange - Callback with {date: string, timezone: string} object
 * @param {object} otherProps - Other props to pass to DatePicker
 */
export function DateFilterInput({ value, onChange, ...otherProps }) {
  // Get user's timezone once
  const userTimezone = React.useMemo(() => getUserTimezone(), []);
  
  // Parse value for picker display
  // Value must be {date: string, timezone: string} or null
  let dateValue = null;
  if (value && typeof value === 'object' && value.date) {
    const dateString = value.date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      if (isValid(localDate)) {
        dateValue = localDate;
      }
    }
  }
  
  const handleChange = (date) => {
    if (date && isValid(date)) {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      onChange({ date: dateString, timezone: userTimezone });
    } else {
      onChange(null);
    }
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <DatePicker
        value={dateValue}
        onChange={handleChange}
        format="yyyy-MM-dd"
        slotProps={{
          textField: {
            size: 'small',
            ...otherProps
          },
          // Configure popper to prevent overflow and ensure proper positioning
          popper: {
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'preventOverflow',
                enabled: true,
              },
            ],
          },
        }}
        {...otherProps}
      />
    </LocalizationProvider>
  );
}

export default DateFilterInput;

